import { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  bulkSetSlotTarget,
  createSlotTemplate,
  deleteSlotTemplate,
  listSlotTemplates,
} from "@/api/catalog";
import { listOrganizations } from "@/api/organizations";
import {
  type Skill,
  listSkills,
  listSlotRequiredSkills,
  setSlotRequiredSkills,
} from "@/api/skills";
import { usePermission } from "@/auth/usePermission";

function diffMinutes(start: string, end: string): number {
  const [sh, sm] = start.split(":").map((x) => parseInt(x, 10));
  const [eh, em] = end.split(":").map((x) => parseInt(x, 10));
  if (sh === undefined || sm === undefined || eh === undefined || em === undefined) return 0;
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60; // overnight
  return mins;
}

export function SlotTemplatesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canWrite = usePermission("slot_template:write");

  const orgs = useQuery({
    queryKey: ["organizations-all-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
  });

  const [orgId, setOrgId] = useState<string>("");

  useEffect(() => {
    if (!orgId && orgs.data?.items.length) {
      const first = orgs.data.items[0];
      if (first) setOrgId(first.id);
    }
  }, [orgs.data, orgId]);

  const slots = useQuery({
    queryKey: ["slot-templates", orgId],
    queryFn: () => (orgId ? listSlotTemplates(orgId) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });

  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState("");
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("13:00");
  const [isNight, setIsNight] = useState(false);
  const [isOvernight, setIsOvernight] = useState(false);
  const [weight, setWeight] = useState("1.0");
  const [serverError, setServerError] = useState<string | null>(null);

  const duration = useMemo(() => diffMinutes(startTime, endTime), [startTime, endTime]);

  const createMutation = useMutation({
    mutationFn: () =>
      createSlotTemplate({
        organization_id: orgId,
        label,
        start_time: `${startTime}:00`,
        end_time: `${endTime}:00`,
        duration_min: duration,
        is_night: isNight,
        is_overnight: isOvernight,
        weight_modifier: parseFloat(weight) || 1.0,
      }),
    onSuccess: () => {
      setShowForm(false);
      setLabel("");
      setServerError(null);
      qc.invalidateQueries({ queryKey: ["slot-templates", orgId] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSlotTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["slot-templates", orgId] }),
  });

  // Standing coverage rule: "N practitioners from HH:MM to HH:MM", applied to
  // every slot of the org inside that range rather than slot by slot.
  const [ruleFrom, setRuleFrom] = useState("08:00");
  const [ruleTo, setRuleTo] = useState("20:00");
  const [ruleTarget, setRuleTarget] = useState(2);
  const [ruleResult, setRuleResult] = useState<string | null>(null);

  const ruleMutation = useMutation({
    mutationFn: () =>
      bulkSetSlotTarget({
        organization_id: orgId,
        from_time: `${ruleFrom}:00`,
        to_time: `${ruleTo}:00`,
        target_headcount: ruleTarget,
      }),
    onSuccess: (r) => {
      setRuleResult(t("settings.targetRuleApplied", { count: r.updated }));
      qc.invalidateQueries({ queryKey: ["slot-templates", orgId] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setRuleResult(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  if (orgs.isLoading) return <CircularProgress />;

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>{t("settings.organization")}</InputLabel>
          <Select
            label={t("settings.organization")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {canWrite && !showForm && orgId && (
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setShowForm(true)}>
            {t("settings.addSlotTemplate")}
          </Button>
        )}
      </Stack>

      {showForm && (
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
          {serverError && <Alert severity="error" sx={{ mb: 2 }}>{serverError}</Alert>}
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label={t("settings.label")} value={label} onChange={(e) => setLabel(e.target.value)} size="small" required sx={{ flex: 1 }} />
              <TextField label={t("settings.startTime")} value={startTime} onChange={(e) => setStartTime(e.target.value)} size="small" type="time" sx={{ width: 130 }} />
              <TextField label={t("settings.endTime")} value={endTime} onChange={(e) => setEndTime(e.target.value)} size="small" type="time" sx={{ width: 130 }} />
              <TextField label={t("settings.duration")} value={duration} size="small" disabled sx={{ width: 110 }} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel control={<Switch checked={isNight} onChange={(e) => setIsNight(e.target.checked)} />} label={t("settings.isNight")} />
              <FormControlLabel control={<Switch checked={isOvernight} onChange={(e) => setIsOvernight(e.target.checked)} />} label={t("settings.isOvernight")} />
              <TextField label={t("settings.weightModifier")} value={weight} onChange={(e) => setWeight(e.target.value)} size="small" type="number" inputProps={{ step: 0.1, min: 0 }} sx={{ width: 180 }} />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
              <Button variant="contained" onClick={() => createMutation.mutate()} disabled={!label || createMutation.isPending}>
                {t("common.save")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {canWrite && orgId && (
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("settings.targetRuleTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("settings.targetRuleHint")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              label={t("settings.startTime")}
              value={ruleFrom}
              onChange={(e) => setRuleFrom(e.target.value)}
              size="small"
              type="time"
              sx={{ width: 130 }}
            />
            <TextField
              label={t("settings.endTime")}
              value={ruleTo}
              onChange={(e) => setRuleTo(e.target.value)}
              size="small"
              type="time"
              sx={{ width: 130 }}
            />
            <TextField
              label={t("settings.targetHeadcount")}
              value={ruleTarget}
              onChange={(e) =>
                setRuleTarget(Math.max(1, Math.min(100, Number(e.target.value) || 1)))
              }
              size="small"
              type="number"
              inputProps={{ min: 1, max: 100, step: 1 }}
              sx={{ width: 180 }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setRuleResult(null);
                ruleMutation.mutate();
              }}
              disabled={ruleMutation.isPending || ruleFrom >= ruleTo}
            >
              {t("settings.targetRuleApply")}
            </Button>
          </Stack>
          {ruleResult && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {ruleResult}
            </Alert>
          )}
        </Box>
      )}

      {slots.isLoading ? (
        <CircularProgress />
      ) : (slots.data ?? []).length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t("settings.noSlotTemplates")}</Typography>
      ) : (
        <List>
          {(slots.data ?? []).map((s) => (
            <ListItem
              key={s.id}
              divider
              secondaryAction={
                canWrite && (
                  <Tooltip title={t("settings.deleteConfirm")}>
                    <IconButton edge="end" color="error" onClick={() => {
                      if (confirm(t("settings.deleteConfirm"))) deleteMutation.mutate(s.id);
                    }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {s.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                      {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip size="small" variant="outlined" label={`${s.duration_min} min`} />
                      <Chip size="small" variant="outlined" label={`× ${s.weight_modifier}`} />
                      <Chip
                        size="small"
                        variant={s.target_headcount > 1 ? "filled" : "outlined"}
                        color={s.target_headcount > 1 ? "primary" : "default"}
                        label={t("settings.targetHeadcountShort", {
                          count: s.target_headcount,
                        })}
                      />
                      {s.is_night && <Chip size="small" color="info" variant="outlined" label="nuit" />}
                      {s.is_overnight && <Chip size="small" color="warning" variant="outlined" label="overnight" />}
                    </Stack>
                    <SlotSkillsLine slotId={s.id} canWrite={canWrite} />
                  </Stack>
                }
                slotProps={{ secondary: { component: "div" } }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}


function SlotSkillsLine({
  slotId,
  canWrite,
}: {
  slotId: string;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Skill[]>([]);

  const required = useQuery({
    queryKey: ["slot-required-skills", slotId],
    queryFn: () => listSlotRequiredSkills(slotId),
  });

  const skills = useQuery({
    queryKey: ["skills", "active"],
    queryFn: () => listSkills(true),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: () => setSlotRequiredSkills(slotId, selected.map((s) => s.id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["slot-required-skills", slotId] });
      qc.invalidateQueries({ queryKey: ["skill-stats"] });
      setOpen(false);
    },
  });

  const openDialog = () => {
    setSelected(required.data ?? []);
    setOpen(true);
  };

  const items = required.data ?? [];

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        {t("settings.requiredSkills")}:
      </Typography>
      {items.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          {t("settings.noRequiredSkills")}
        </Typography>
      )}
      {items.map((s) => (
        <Chip
          key={s.id}
          size="small"
          label={s.code}
          sx={{
            bgcolor: s.color ?? undefined,
            color: s.color ? "#fff" : undefined,
            fontFamily: "monospace",
          }}
        />
      ))}
      {canWrite && (
        <IconButton size="small" onClick={openDialog} sx={{ p: 0.25 }}>
          <EditIcon fontSize="inherit" sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("settings.editRequiredSkillsTitle")}</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={skills.data ?? []}
            value={selected}
            onChange={(_, v) => setSelected(v)}
            getOptionLabel={(o) => `${o.code} — ${o.label}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.code}
                    size="small"
                    sx={{
                      bgcolor: option.color ?? undefined,
                      color: option.color ? "#fff" : undefined,
                      fontFamily: "monospace",
                    }}
                    {...chipProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("settings.requiredSkills")}
                placeholder={t("settings.requiredSkillsPlaceholder")}
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
